import { FC } from 'react';
import {
  Navigate,
  Route,
  RouteProps,
  Routes,
  useParams,
} from 'react-router-dom';
import { useConfigContext } from '@fe-core';
import { Tabs } from '@fe-learner-common/components/Tabs';
import { TSessionDetails } from '@api/io/session.io';
import { NextGenUiToggle } from '@components/NextGenUiToggle';
import { OverviewTab } from '@components/tabs/overview/OverviewTab';
import { InstructorsTab } from '@components/tabs/InstructorsTab';
import { ParticipantsTab } from '@components/tabs/ParticipantsTab';
import { EventsTab } from '@components/tabs/events/EventsTab';
import { SessionDetailsPage } from '@routes/session-details/SessionDetailsPage';
import { tabMap } from '@utils/tabMap';

export type TTabKeys = 'overview' | 'participants' | 'instructors' | 'events';
type TTabRoute = RouteProps & {
  enabled?: boolean;
};

type CourseSessionDetailsProps = {
  sessionDetails: TSessionDetails;
};

export const CourseSessionDetails: FC<CourseSessionDetailsProps> = ({
  sessionDetails,
}) => {
  const selectedTab = useParams()['*'];
  const { extension } = useConfigContext();
  const hiddenTabs = extension?.['fe-learner-catalog']?.hiddenTabs ?? [];

  const tabRoutes: Record<TTabKeys, TTabRoute> = {
    overview: {
      enabled: true,
      path: 'overview',
      element: <OverviewTab sessionDetails={sessionDetails} />,
    },
    instructors: {
      enabled: true,
      path: 'instructors',
      element: <InstructorsTab persons={sessionDetails.trainers} />,
    },
    participants: {
      enabled:
        sessionDetails.participantInfo?.enabled &&
        sessionDetails.participantInfo?.showThumbnail,
      path: 'participants',
      element: <ParticipantsTab persons={sessionDetails.participants} />,
    },
    events: {
      enabled: true,
      path: 'events',
      element: <EventsTab dates={sessionDetails.dates} />,
    },
  };

  const availableTabs = Object.entries(tabRoutes).filter(
    ([tab, route]) => route.enabled && !hiddenTabs.includes(tab),
  );
  const availableTabKeys = availableTabs.map(([tab]) => tab as TTabKeys);
  const [firstAvailableTabKey] = availableTabKeys;
  const tabs = availableTabKeys.map((key) => tabMap[key]);

  return (
    <Routes>
      <Route
        element={
          <>
            <NextGenUiToggle isLoading={false} />
            <SessionDetailsPage sessionDetails={sessionDetails}>
              <Tabs tabs={tabs} selectedTab={selectedTab} />
            </SessionDetailsPage>
          </>
        }
      >
        {availableTabs.map(([tabKey, route]) => (
          <Route key={tabKey} {...route} />
        ))}
      </Route>
      <Route
        path="*"
        element={<Navigate to={firstAvailableTabKey} replace />}
      />
    </Routes>
  );
};
